// JEDNO miejsce, w ktorym czytamy "gdzie jestesmy" z DI (DESIGN §2.1).
//
// Prefiks locale liczy sie z ADRESU (wariant B), a adres da sie odczytac dwoma
// kanalami:
//  - PlatformLocation.pathname (+ search/hash) — dziala w przegladarce ORAZ na SSR,
//  - framework token REQUEST (@angular/core) — zapasowo, gdy pierwszy nic nie mowi.
// NIGDY translate.currentLang — to bylby cykl (jezyk z adresu, adres z jezyka).
//
// 🔴 SPROSTOWANIE: wczesniejszy komentarz twierdzil, ze na SSR przed hydracja
// PlatformLocation "zwraca '/' i sam w sobie klamie". To NIEPRAWDA na sciezce
// renderu. Sprawdzone w ZAINSTALOWANEJ bibliotece:
//   node_modules/@angular/platform-server/fesm2022/_server-chunk.mjs:281-319
//     class ServerPlatformLocation { pathname = '/'; ... constructor() {
//       const config = inject(INITIAL_CONFIG, {optional:true});
//       if (!config) return;  if (config.url) { ... this.pathname = pathname; ... } }
//   node_modules/@angular/ssr/fesm2022/ssr.mjs:118-124 (renderAngular)
//     provide: INITIAL_CONFIG, useValue: { url: urlToRender.href, document: html }
// Czyli przy KAZDYM realnym renderze INITIAL_CONFIG.url jest ustawiony i pathname
// niesie PRAWDZIWA sciezke ('/en/menu'), razem z search i hash.
//
// '/' pojawia sie w JEDNYM stanie: build-time route extraction, gdzie
// getRoutesFromAngularRouterConfig() podaje url `${protocol}//${host}/`
// (ssr.mjs:720-731) — i tam REQUEST tez nie istnieje. Dlatego galaz REQUEST ponizej
// NIE jest ratunkiem przed klamstwem PlatformLocation, tylko pasem bezpieczenstwa
// na wypadek renderu bez INITIAL_CONFIG (np. inny host SSR/testy). Oba kanaly,
// gdy sa obecne naraz, musza dawac TEN SAM wynik — przypiete w current-url.spec.ts.
//
// Funkcja MUSI dzialac w kontekscie wstrzykiwania (konstruktor / factory) i NIGDY
// nie rzuca: brak obu providerow to stan normalny przy build-time route extraction
// (@angular/ssr), a wyjatek w tym miejscu wywraca BUILD, nie strone (F6).
import { REQUEST, inject } from '@angular/core';
import { PlatformLocation } from '@angular/common';

import { splitLocalePrefix } from './detect-locale';

/**
 * Zwraca biezacy adres jako string `pathname[?search][#hash]` (albo '' gdy nie wiadomo).
 * Wynik jest przeznaczony do podania wprost do `splitLocalePrefix()` — ta funkcja
 * sama odcina query/fragment, wiec nie normalizujemy tu niczego na wlasna reke.
 */
export function readCurrentUrlOnce(): string {
  let pathname = '';
  let suffix = '';
  try {
    const location = inject(PlatformLocation, { optional: true });
    pathname = location?.pathname ?? '';
    suffix = `${location?.search ?? ''}${location?.hash ?? ''}`;
  } catch {
    pathname = '';
    suffix = '';
  }
  if (!pathname || pathname === '/') {
    try {
      const req = inject(REQUEST, { optional: true }) as { url?: string } | null;
      const raw = req?.url ?? '';
      if (raw) {
        // REQUEST.url bywa sciezka wzgledna ('/en/menu?c=1') albo pelnym URL-em.
        if (raw.startsWith('http')) {
          const parsed = new URL(raw);
          pathname = parsed.pathname;
          suffix = parsed.search;
        } else {
          pathname = raw;
          suffix = '';
        }
      }
    } catch {
      /* brak REQUEST — build-time extraction (F6). Zostaje to, co bylo. */
    }
  }
  return `${pathname}${suffix}`;
}

/**
 * Locale BIEZACEGO adresu — 'en' tylko dla prefiksu /en, w kazdym innym
 * przypadku 'pl' (regula: bez prefiksu jest polski, patrz detect-locale.ts).
 *
 * MUSI byc wolane w kontekscie wstrzykiwania (konstruktor komponentu/serwisu),
 * bo opiera sie na readCurrentUrlOnce(). Istnieje po to, zeby SSR i przegladarka
 * liczyly jezyk kafelka z TEGO SAMEGO zrodla (adresu), a nie z translate.currentLang
 * — ten ostatni w przegladarce zalezy jeszcze od localStorage i po hydracji
 * potrafi sie rozjechac z tym, co poszlo w HTML-u z SSR.
 */
export function readCurrentLocaleOnce(): 'pl' | 'en' {
  return splitLocalePrefix(readCurrentUrlOnce()).locale === 'en' ? 'en' : 'pl';
}

/**
 * Adresy obu wersji jezykowych dla podanego adresu biezacego (T8).
 *
 * Router STRUKTURALNIE nie potrafi wyrazic drugiego locale: serialize() dokleja
 * prefiks zamrozony w konstruktorze LocaleUrlSerializer, wiec kazda nawigacja SPA
 * zostaje w tym samym drzewie jezykowym. Przelaczenie jezyka to zmiana ADRESU
 * i pelne przeladowanie dokumentu — a te adresy licza sie wlasnie tutaj.
 *
 * Query i fragment przechodza bez zmian (splitLocalePrefix przenosi je do `rest`),
 * bo magic-linki rezerwacji (?c=...) musza przezyc zmiane jezyka.
 */
export function buildLangHrefs(currentUrl: string): Record<'pl' | 'en', string> {
  const rest = splitLocalePrefix(currentUrl).rest || '/';
  return { pl: rest, en: `/en${rest}` };
}
